import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { T, FONT_STACK } from '../containers/customer/customers.theme';
import { CustomersKeyframes, PeopleIcon, PrimaryButton } from '../containers/customer/CustomersUI';
import CustomerList from '../containers/customer/CustomerList';
import CustomerLedger from '../containers/customer/CustomerLedger';
import TransactionDialog from '../containers/customer/TransactionDialog';
import CustomerFormDialog from '../containers/customer/CustomerFormDialog';
import DeleteTransactionDialog from '../containers/customer/DeleteTransactionDialog';
import customerStatementPdf from '../containers/customer/customerStatementPdf';
import {
  addCustomer,
  addCustomerTransaction,
  removeCustomerTransaction,
  replaceCustomerTransaction,
  setCustomerDataFetched,
  setCustomerTransactions,
  setCustomers,
  updateCustomer,
} from '../containers/customer/customerSlice';
import {
  createCustomer,
  createTransaction,
  deleteTransaction,
  fetchCustomers,
  fetchTransactions,
  updateCustomer as patchCustomer,
  updateTransaction,
  withRunningBalance,
} from '../containers/customer/customersApi';

/**
 * The customers book — the phone's Customers screens, side by side.
 *
 * On the phone this is two pages: a list you tap into, and the ledger you land
 * on. A till has room for both at once, and working a book means hopping
 * between people, so the list stays put and the panel beside it changes.
 *
 * Everything the phone's module does with money is here: the list, the search,
 * adding and editing a customer, the four-column ledger, and Bixin / Deen
 * Cusub. What is not here is the phone's employee-name field and its
 * over-limit password step, left out on purpose for a plainer flow.
 */
export default function Customers() {
  const dispatch = useDispatch();

  const { business } = useSelector((state) => state.login.activeUser);
  const activeUser = useSelector((state) => state.login.activeUser);
  const token = useSelector((state) => state.login.token);

  const { customers, isCustomerDataFetched, transactionsByCustomer } = useSelector(
    (state) => state.customers
  );

  const [query, setQuery] = React.useState('');
  const [selectedId, setSelectedId] = React.useState(null);
  const [loadingList, setLoadingList] = React.useState(false);
  const [loadingLedger, setLoadingLedger] = React.useState(false);
  const [listError, setListError] = React.useState('');

  const [transactionKind, setTransactionKind] = React.useState(null);
  const [editingTransaction, setEditingTransaction] = React.useState(null);
  const [deletingTransaction, setDeletingTransaction] = React.useState(null);
  const [editing, setEditing] = React.useState(null);
  const [creating, setCreating] = React.useState(false);

  const currency = business?.currencyMark || '$';
  const selected = customers.find((customer) => customer._id === selectedId) || null;
  const transactions = selectedId ? transactionsByCustomer[selectedId] || [] : [];

  /* ── the list ───────────────────────────────────────────────────────── */

  React.useEffect(() => {
    if (isCustomerDataFetched || !business?._id) return;

    let cancelled = false;
    setLoadingList(true);

    fetchCustomers({ businessId: business._id, token })
      .then((rows) => {
        if (cancelled) return;
        dispatch(setCustomers(rows));
        dispatch(setCustomerDataFetched(true));
      })
      .catch((err) => {
        if (cancelled) return;
        setListError(err?.response?.data?.message || 'Could not load customers');
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isCustomerDataFetched, business, token, dispatch]);

  /* ── one customer's book, fetched only when it is opened ────────────── */

  const loadLedger = React.useCallback(
    (customerId, { force } = {}) => {
      if (!customerId) return;
      if (!force && transactionsByCustomer[customerId]) return;

      setLoadingLedger(true);
      fetchTransactions({ customerId, token })
        .then((rows) => {
          dispatch(
            setCustomerTransactions({
              customerId,
              transactions: withRunningBalance(rows),
            })
          );
        })
        .catch(() => {
          dispatch(setCustomerTransactions({ customerId, transactions: [] }));
        })
        .finally(() => setLoadingLedger(false));
    },
    [transactionsByCustomer, token, dispatch]
  );

  const openCustomer = (customer) => {
    setSelectedId(customer._id);
    loadLedger(customer._id);
  };

  /* ── writing ────────────────────────────────────────────────────────── */

  /**
   * One handler for adding and for correcting, because the dialog is the same
   * dialog — it passes back the line it was editing, or nothing for a new one.
   *
   * The running balance belongs to the whole list rather than to any one line,
   * so after either it is recomputed from scratch instead of patched.
   */
  const submitTransaction = async ({ kind, amount, description, date, transaction }) => {
    if (transaction) {
      const saved = await updateTransaction({
        id: transaction._id,
        kind,
        amount,
        description,
        date,
        token,
      });

      const merged = { ...transaction, ...(saved || {}) };
      dispatch(replaceCustomerTransaction({ customerId: selectedId, transaction: merged }));

      const next = withRunningBalance(
        (transactionsByCustomer[selectedId] || []).map((entry) =>
          entry._id === transaction._id ? merged : entry
        )
      );
      dispatch(setCustomerTransactions({ customerId: selectedId, transactions: next }));
      return;
    }

    const created = await createTransaction({
      customerId: selectedId,
      userId: activeUser?._id || activeUser?.id,
      kind,
      amount,
      description,
      date,
      token,
    });

    dispatch(addCustomerTransaction({ customerId: selectedId, transaction: created }));

    const next = withRunningBalance([...(transactionsByCustomer[selectedId] || []), created]);
    dispatch(setCustomerTransactions({ customerId: selectedId, transactions: next }));
  };

  const removeTransaction = async (transaction) => {
    await deleteTransaction({ id: transaction._id, token });

    dispatch(
      removeCustomerTransaction({ customerId: selectedId, transactionId: transaction._id })
    );

    const next = withRunningBalance(
      (transactionsByCustomer[selectedId] || []).filter(
        (entry) => entry._id !== transaction._id
      )
    );
    dispatch(setCustomerTransactions({ customerId: selectedId, transactions: next }));
  };

  const submitNewCustomer = async (payload) => {
    const created = await createCustomer({
      payload: { ...payload, business: business?._id },
      token,
    });
    dispatch(addCustomer(created));
    if (created?._id) {
      setSelectedId(created._id);
      dispatch(setCustomerTransactions({ customerId: created._id, transactions: [] }));
    }
  };

  const submitEdit = async (payload) => {
    const saved = await patchCustomer({ id: editing._id, payload, token });
    dispatch(updateCustomer({ _id: editing._id, updatedCustomer: saved || payload }));
  };

  const printStatement = (customer) => {
    customerStatementPdf({
      customer,
      transactions: transactionsByCustomer[customer._id] || [],
      business,
    });
  };

  /* ── search ─────────────────────────────────────────────────────────── */

  const visible = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    const rows = customers.slice().sort((a, b) => {
      // Most recently touched first, as the phone sorts it.
      const at = new Date(a.updated_at || a.updatedAt || 0).getTime();
      const bt = new Date(b.updated_at || b.updatedAt || 0).getTime();
      return bt - at;
    });
    if (!q) return rows;
    return rows.filter(
      (customer) =>
        (customer.name || '').toLowerCase().includes(q) ||
        (customer.phone || '').toLowerCase().includes(q) ||
        (customer.district || '').toLowerCase().includes(q) ||
        (customer.damiinName || '').toLowerCase().includes(q)
    );
  }, [customers, query]);

  /* ── the book is empty: nothing to list beside ──────────────────────── */

  if (isCustomerDataFetched && !customers.length) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          height: 'calc(100vh - 90px)',
          background: T.canvas,
          fontFamily: FONT_STACK,
        }}
      >
        <CustomersKeyframes />
        <span
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 56,
            height: 56,
            border: `1.5px dashed ${T.borderStrong}`,
            borderRadius: T.radiusPill,
            color: T.inkGhost,
          }}
        >
          <PeopleIcon />
        </span>
        <span style={{ fontSize: 16, color: T.inkSoft }}>Majiraan Macaamiil</span>
        <PrimaryButton onClick={() => setCreating(true)} style={{ padding: '0 24px' }}>
          + Ku Dar Macaamiil
        </PrimaryButton>

        {creating ? (
          <CustomerFormDialog onClose={() => setCreating(false)} onSubmit={submitNewCustomer} />
        ) : null}
      </div>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        height: 'calc(100vh - 90px)',
        background: T.canvas,
        color: T.ink,
        fontFamily: FONT_STACK,
        overflow: 'hidden',
      }}
    >
      <CustomersKeyframes />

      <CustomerList
        customers={visible}
        currency={currency}
        query={query}
        onQueryChange={setQuery}
        selectedId={selectedId}
        onOpen={openCustomer}
        onAdd={() => setCreating(true)}
        loading={loadingList}
      />

      {listError ? (
        <div
          style={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 40,
            textAlign: 'center',
          }}
        >
          <span style={{ fontSize: 14, color: T.danger }}>{listError}</span>
        </div>
      ) : (
        <CustomerLedger
          customer={selected}
          transactions={transactions}
          loading={loadingLedger}
          currency={currency}
          onPayment={() => setTransactionKind('payment')}
          onCharge={() => setTransactionKind('charge')}
          onEdit={setEditing}
          onStatement={printStatement}
          onEditTransaction={setEditingTransaction}
          onDeleteTransaction={setDeletingTransaction}
        />
      )}

      {transactionKind && selected ? (
        <TransactionDialog
          kind={transactionKind}
          customer={selected}
          currency={currency}
          onClose={() => setTransactionKind(null)}
          onSubmit={submitTransaction}
        />
      ) : null}

      {editingTransaction && selected ? (
        <TransactionDialog
          // Which side it sits on comes from the line itself, not from a
          // choice — a correction never flips a charge into a payment.
          kind={Number(editingTransaction.debit || 0) > 0 ? 'charge' : 'payment'}
          customer={selected}
          currency={currency}
          transaction={editingTransaction}
          onClose={() => setEditingTransaction(null)}
          onSubmit={submitTransaction}
        />
      ) : null}

      {deletingTransaction && selected ? (
        <DeleteTransactionDialog
          transaction={deletingTransaction}
          customer={selected}
          currency={currency}
          onClose={() => setDeletingTransaction(null)}
          onConfirm={removeTransaction}
        />
      ) : null}

      {creating ? (
        <CustomerFormDialog onClose={() => setCreating(false)} onSubmit={submitNewCustomer} />
      ) : null}

      {editing ? (
        <CustomerFormDialog
          customer={editing}
          onClose={() => setEditing(null)}
          onSubmit={submitEdit}
        />
      ) : null}
    </div>
  );
}
